// Minimal REST helpers for the backend /api/v1 endpoints.
//
// Auth: the logged-in user's access token (localStorage) is attached as a
// bearer on every request. If none is present we fall back to the optional
// build-time WS_TOKEN so the historical unauthenticated setup keeps working.
// Before each request we proactively refresh an expired/near-expiry access token
// (see freshAuthHeader) so the optional-auth analyze endpoints attribute the run
// to the user instead of recording it anonymous. As a backstop, a 401 also
// transparently refreshes once and retries the original request; if the refresh
// fails the tokens are cleared and the 401 is surfaced so the app can bounce back
// to the login screen.
import { API_V1, WS_TOKEN } from 'config';
import { getAuthToken, getRefreshToken, clearTokens } from 'auth/tokenStorage';
import { refreshAccessToken } from 'auth/refresh';
import { isAccessTokenExpired } from 'auth/jwt';

function authHeader(): Record<string, string> {
  const token = getAuthToken() || WS_TOKEN;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Auth header for the next request, refreshing the access token first if it has
 * expired (or is about to). This matters for the optional-auth analyze endpoints:
 * they never return 401 for an expired token — they just record the run as
 * anonymous — so the reactive 401-refresh below would never recover attribution.
 * Single-flight is handled inside refreshAccessToken(); a failed/transient
 * refresh falls through to the (stale) header so the request still proceeds.
 */
async function freshAuthHeader(): Promise<Record<string, string>> {
  if (isAccessTokenExpired(getAuthToken()) && getRefreshToken()) {
    await refreshAccessToken();
  }
  return authHeader();
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    let detail = text;
    try {
      const j = JSON.parse(text);
      detail = j.detail ?? j.message ?? text;
    } catch {
      /* keep raw text */
    }
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  return (text ? JSON.parse(text) : {}) as T;
}

interface RequestOpts {
  method: string;
  /** Pre-serialized body (already a string) or undefined for bodyless requests. */
  body?: string;
  signal?: AbortSignal;
  /** Extra headers merged after the defaults (e.g. Content-Type). */
  headers?: Record<string, string>;
}

/**
 * Core fetch wrapper: attaches auth + JSON headers, and on a 401 refreshes the
 * access token once and replays the request. `_retried` guards against an
 * infinite loop if the refreshed token is also rejected.
 */
async function request<T>(url: string, opts: RequestOpts, _retried = false): Promise<T> {
  const auth = await freshAuthHeader();
  const res = await fetch(url, {
    method: opts.method,
    headers: { 'Content-Type': 'application/json', ...auth, ...(opts.headers ?? {}) },
    body: opts.body,
    signal: opts.signal,
  });

  if (res.status === 401 && !_retried) {
    const result = await refreshAccessToken();
    if (result.ok) {
      return request<T>(url, opts, true);
    }
    // Refresh failed permanently — drop tokens so the UI re-gates on login.
    clearTokens();
  }

  return parse<T>(res);
}

/** GET a JSON resource under /api/v1. */
export async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(`${API_V1}${path}`, { method: 'GET', signal });
}

/** Filename from a Content-Disposition header, if the server sent one. */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  // RFC 5987 `filename*=UTF-8''…` takes precedence over the plain form.
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      /* malformed encoding — fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

/**
 * GET a binary resource (file download) under /api/v1.
 *
 * Deliberately does NOT go through `request()`/`parse()`: those always read the
 * body as text and JSON.parse it, which would corrupt a binary payload. We keep
 * the same auth behaviour — proactive refresh plus a single reactive 401 refresh
 * and replay — and surface failures as the same `ApiError` as every other call.
 *
 * `timeoutMs` defaults high because a full-table export is genuinely slow to
 * build server-side.
 */
export async function getBlob(
  path: string,
  signal?: AbortSignal,
  timeoutMs = 600_000,
  _retried = false,
): Promise<{ blob: Blob; filename: string | null }> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });

  try {
    const auth = await freshAuthHeader();
    const res = await fetch(`${API_V1}${path}`, {
      method: 'GET',
      headers: { ...auth },
      signal: ctrl.signal,
    });

    if (res.status === 401 && !_retried) {
      const result = await refreshAccessToken();
      if (result.ok) {
        clearTimeout(timer);
        return getBlob(path, signal, timeoutMs, true);
      }
      clearTokens();
    }

    if (!res.ok) {
      // Errors are JSON even on a binary endpoint — parse() always throws here.
      return await parse<{ blob: Blob; filename: string | null }>(res);
    }

    return {
      blob: await res.blob(),
      filename: filenameFromDisposition(res.headers.get('Content-Disposition')),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * POST a JSON body under /api/v1.
 *
 * `timeoutMs` bounds requests that can legitimately run for minutes (a fork is
 * one Claude call); omit it to inherit the browser's own behaviour, which is what
 * every short request wants.
 */
export async function postJson<T>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
  timeoutMs?: number,
): Promise<T> {
  const opts = { method: 'POST', body: JSON.stringify(body ?? {}), signal };
  if (timeoutMs == null) return request<T>(`${API_V1}${path}`, opts);

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  try {
    return await request<T>(`${API_V1}${path}`, { ...opts, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** PATCH a JSON body under /api/v1. */
export async function patchJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(`${API_V1}${path}`, { method: 'PATCH', body: JSON.stringify(body ?? {}), signal });
}

/** DELETE a resource under /api/v1. */
export async function del<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(`${API_V1}${path}`, { method: 'DELETE', signal });
}

/** POST with query params and an empty body (matches the analyze endpoints). */
export async function postQuery<T>(
  path: string,
  params: Record<string, string | number | boolean>,
  signal?: AbortSignal,
  timeoutMs = 360_000,
): Promise<T> {
  const qs = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ).toString();

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });

  try {
    return await request<T>(`${API_V1}${path}?${qs}`, { method: 'POST', body: '{}', signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * POST a multipart/form-data body under /api/v1 (file uploads).
 *
 * Deliberately does NOT go through `request()`: that helper hardcodes
 * `Content-Type: application/json`, which would clobber the multipart boundary
 * the browser must set itself. We attach auth only and let `fetch` build the
 * Content-Type from the FormData. We still refresh a stale access token up front
 * (so v0 image runs are attributed to the user, not recorded anonymous), but
 * there's no reactive 401-retry here — a 401 surfaces as an ApiError for the
 * caller to handle.
 */
export async function postForm<T>(
  path: string,
  form: FormData,
  signal?: AbortSignal,
  timeoutMs = 360_000,
): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });

  try {
    const auth = await freshAuthHeader();
    const res = await fetch(`${API_V1}${path}`, {
      method: 'POST',
      headers: { ...auth },
      body: form,
      signal: ctrl.signal,
    });
    return await parse<T>(res);
  } finally {
    clearTimeout(timer);
  }
}
